import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { Box, TextField, Typography, Theme, Button, Link } from '@mui/material';
import { useSnapshot } from 'valtio';
import { makeStyles } from 'tss-react/mui';
import ButtonGroup from '../../common/components/ButtonGroup';
import { IssueDetails } from '../../issues/common/types/issues-types';
import { resolveRepoIcon } from 'common-icons';
import { RepoTypes } from 'common-types';
import { updatePRComment } from '../store-actions/quick-fix-store-actions';
import QuickFixStore from '../stores/quick-fix-store';
import { createPullRequest } from '../actions/quick-fix-actions';
import { BiCodeAlt as BiCodeAltIcon } from 'react-icons/bi';
import FixFiles from '../components/FixFiles';
import FixDescription from '../components/FixDescription';
import { useAuth0 } from '@auth0/auth0-react';
import { AggregationItem, Nullable } from 'common-types';
import ExternalLinkButton from '../../elements/ExternalLinkButton/ExternalLinkButton';

const CreatePRFormContainer: FC<
  React.PropsWithChildren<CreatePRFormContainerProps>
> = props => {
  const { onClose, selectedIssue, isReadOnlyRole, onViewCodeFix } = props;
  const { classes } = useStyles();

  const { prComment, prTitle, prBody, isLoadingCreatePR, viewFileFixes } =
    useSnapshot(QuickFixStore, {
      sync: true,
    });

  const { user } = useAuth0();
  const RepoIcon = resolveRepoIcon(
    selectedIssue?.app?.type as RepoTypes,
    selectedIssue?.app.fakeApp,
  );

  const onCommentChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      updatePRComment(event.target.value);
    },
    [],
  );

  const fileNames = useMemo(
    () => [...new Set(viewFileFixes.map(f => f.filePath))],
    [viewFileFixes],
  );

  const onCreate = useCallback(() => {
    createPullRequest(user, onClose);
  }, [onClose, user]);

  const buttonActionTooltip = isReadOnlyRole
    ? 'Read only user cannot create pull requests'
    : 'Create pull request';

  if (!selectedIssue) {
    return null;
  }

  return (
    <>
      <Box display='flex' flexDirection='row' gap={5}>
        <FixFiles files={fileNames} />
        <Box
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
          flex={1}>
          <FixDescription title={prTitle} description={prBody} />
          <Box display='flex' mt={2} mb={2} ml={2} alignItems='start'>
            <div>{RepoIcon && <RepoIcon className={classes.repoIcon} />}</div>
            <Typography variant='body2' ml={2}>
              {selectedIssue.app.name}
            </Typography>
          </Box>
          <Box mb={2} width='100%'>
            <Typography mb={0.5}>Comment</Typography>
            <TextField
              multiline
              rows={1}
              fullWidth
              value={prComment}
              onChange={onCommentChange}
              disabled={isLoadingCreatePR}
            />
          </Box>
          {onViewCodeFix && (
            <Button
              color='primary'
              startIcon={<BiCodeAltIcon />}
              onClick={onViewCodeFix}
              variant='text'>
              View Code Fix
            </Button>
          )}
          {selectedIssue.fixIssue?.activeFix?.fixURL && (
            <Typography variant='body2'>
              Merge Request already available:{' '}
              <Link variant='body2'>
                {selectedIssue.fixIssue.activeFix.fixURL}
              </Link>
              <ExternalLinkButton
                href={selectedIssue.fixIssue.activeFix.fixURL}
                target='_blank'
                rel='noreferrer'
                sx={{ fontSize: 'inherit' }}
                color='inherit'
              />
            </Typography>
          )}
        </Box>
      </Box>
      <Box display='flex' alignItems='end'>
        <ButtonGroup
          onCancel={onClose}
          onClick={onCreate}
          isLoading={isLoadingCreatePR}
          buttonCancelLabel='Cancel'
          buttonCancelTooltip='Cancel operation'
          buttonActionLabel='Create'
          buttonActionTooltip={buttonActionTooltip}
          buttonActionColor='primary'
          isActionDisabled={isReadOnlyRole}
        />
      </Box>
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  repoIcon: {
    width: '1rem',
    height: '1rem',
    flexShrink: 0,
  },
}));

interface CreatePRFormContainerProps {
  selectedIssue: Nullable<IssueDetails>;
  selectedAggs: AggregationItem[];
  onClose: () => void;
  isReadOnlyRole: boolean;
  onViewCodeFix?: () => void;
}

export default CreatePRFormContainer;
